import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    OnInit,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BazisInputOptionsComponent } from '@bazis/form/components/base/options/input-options.component';
import { EntData } from '@bazis/shared/models/srv.types';
import { combineLatest, Observable, shareReplay } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { GroupedOption, GroupedOptionsSettings } from '@bazis/form/models/form.types';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';

@UntilDestroy()
@Component({
    selector: 'bazis-input-options-grouped',
    templateUrl: './input-options-grouped.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisInputOptionsGroupedComponent
    extends BazisInputOptionsComponent
    implements OnChanges, OnInit
{
    @Input()
    groupedOptionsSettings: GroupedOptionsSettings = null;

    showGroup: Array<boolean> = [];

    groupedOptions$ = this.needUpdateOptionsList$.pipe(
        filter(
            (v) =>
                !!this.groupedOptionsSettings &&
                (!!this.entityType || !!this.groupedOptionsSettings.entityType),
        ),
        switchMap(() =>
            combineLatest([
                this.entityService.getAllEntitiesList$(
                    this.groupedOptionsSettings.parentType,
                    false,
                    true,
                ),
                this.entityService.getAllEntitiesList$(
                    this.groupedOptionsSettings.entityType || this.entityType,
                    false,
                    true,
                ),
            ]),
        ),
        map(([allParents, allChildren]) => {
            const groups = Array.from(
                new Set(
                    allChildren.list.map(
                        (v) => v.$snapshot[this.groupedOptionsSettings.connectionField].id,
                    ),
                ),
            );
            return groups.map((parentId) => ({
                groupEntity: allParents.list.find((v) => v.id === parentId),
                groupOptions: allChildren.list.filter(
                    (v) => v.$snapshot[this.groupedOptionsSettings.connectionField].id === parentId,
                ),
            }));
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    groups$: Observable<GroupedOption[]> = this.groupedOptions$.pipe(
        map((groupedOptions) => {
            let groupedList = JSON.parse(JSON.stringify(groupedOptions));
            groupedList.forEach(() => {
                this.showGroup.push(true);
            });

            if (this.includeOnly) {
                groupedList.forEach((group) => {
                    group.groupOptions = group.groupOptions.filter(
                        (entity) => this.includeOnly.indexOf(entity.id) > -1,
                    );
                });
            }

            if (this.excludeItems) {
                groupedList.forEach((group) => {
                    group.groupOptions = group.groupOptions.filter(
                        (entity) => this.excludeItems.indexOf(entity.id) === -1,
                    );
                });
            }

            const notEmptyGroups = groupedList.filter((v) => v.groupOptions.length > 0);
            const list = notEmptyGroups.length > 0 ? notEmptyGroups : null;

            this.allValues = list
                ? list.reduce((acc, group) => acc.concat(group.groupOptions.map((v) => v.id)), [])
                : [];

            if (this.resetSelectionOnUpdateOptions === 'reset') {
                this.toggleValue('');
            }
            if (this.resetSelectionOnUpdateOptions === 'keepExistedInNewList') {
                this.setNewValues(
                    this.values._.filter(
                        (value) => this.allValues.findIndex((v) => v === value) > -1,
                    ),
                );
            }
            return list;
        }),
    );

    sortedGroups$: Observable<GroupedOption[]> = this.groups$.pipe(
        map((groups) => {
            return groups.sort((a, b) =>
                a.groupEntity?.$snapshot?.name < b.groupEntity?.$snapshot?.name
                    ? -1
                    : a.groupEntity?.$snapshot?.name > b.groupEntity?.$snapshot?.name
                    ? 1
                    : 0,
            );
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    selectedValues$: Observable<EntData[]> = combineLatest([this.groups$, this.values.$]).pipe(
        map(([groups, vals]) => {
            if (!groups || !groups.length) return [];

            const selected = !this.values._.length
                ? this.captionEmpty
                    ? [null]
                    : this.values._
                : this.values._;

            let values = [];

            groups.forEach((group) => {
                values = values.concat(
                    group.groupOptions.filter((v) => selected.indexOf(v.id) > -1),
                );
            });
            return values;
        }),
    );

    ngOnInit() {
        super.ngOnInit();
        if (this.groupedOptionsSettings) this.needUpdateOptionsList$.next(true);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            (changes.groupedOptionsSettings &&
                changes.groupedOptionsSettings.previousValue !==
                    changes.groupedOptionsSettings.currentValue) ||
            (changes.entityType &&
                changes.entityType.previousValue !== changes.entityType.currentValue)
        ) {
            this.needUpdateOptionsList$.next(true);
        }
    }

    toggleGroup(index) {
        this.showGroup[index] = !this.showGroup[index];
    }

    toggleValueInGroup(id, ev) {
        ev.stopImmediatePropagation();
        this.toggleValue(id);
    }
}
